import React, { useEffect, useState } from "react";
import { api, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Boxes, TriangleAlert, Package, ClipboardCheck, ArrowUpDown, Coins } from "lucide-react";
import { Link } from "react-router-dom";

export default function InventoryLandingPage() {
  const { branchId } = useAuth();
  const [stats, setStats] = useState(null);
  const [balances, setBalances] = useState([]);
  const [wastage, setWastage] = useState([]);

  const load = async () => {
    if (!branchId) return;
    try {
      const [s, b, w] = await Promise.all([
        api.get("/inventory/stats", { params: { branch_id: branchId } }),
        api.get("/inventory/balances", { params: { branch_id: branchId } }),
        api.get("/inventory/movements", { params: { branch_id: branchId, type: "wastage", limit: 5 } }),
      ]);
      setStats(s.data); setBalances(b.data); setWastage(w.data);
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [branchId]);

  const lows = balances.filter((b) => b.low_stock).slice(0, 5);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="inventory-landing">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Inventory · Overview</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Stock &amp; wastage</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Track ingredients and drinks by base unit, receive shipments, log wastage with reason codes, and close the day with a blind count that generates variance corrections automatically.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Stock value on hand</div>
          <div className="mt-3 display-heading text-5xl ink mono" data-testid="inv-total-value">
            {peso(stats?.total_value ?? 0)}
          </div>
          <div className="mt-1 tiny-label ink-subtle">
            {stats?.tracked_items ?? 0} items tracked · {stats?.low_stock ?? 0} low · {stats?.zero_stock ?? 0} out
          </div>
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first — inventory balances are per-branch.
        </div>
      )}

      {branchId && (
        <>
          {/* Stat strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-12 gap-y-6 animate-fade-up animate-fade-up-1">
            <Stat num=".01" label="Items tracked" value={stats?.tracked_items ?? 0}/>
            <Stat num=".02" label="Low-stock" value={stats?.low_stock ?? 0} warn={stats?.low_stock > 0}/>
            <Stat num=".03" label="Wastage cost · 7d" value={peso(stats?.wastage_cost_7d ?? 0)} accent/>
            <Stat num=".04" label="Open blind counts" value={stats?.open_count_sessions ?? 0}/>
          </div>

          {/* Two column: low-stock + recent wastage */}
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14 animate-fade-up animate-fade-up-2">
            <div>
              <div className="section-eyebrow mb-4 flex items-center justify-between">
                <span>Low-stock alerts</span>
                <Link to="/inventory/balances" className="tiny-label ink-subtle hover:ink">See all →</Link>
              </div>
              {lows.length === 0 ? (
                <div className="ink-subtle text-sm">No items below their threshold.</div>
              ) : (
                <div className="divide-y hairline-soft border-y hairline-soft" data-testid="inv-low-list">
                  {lows.map((b) => (
                    <div key={b.item_id} className="py-3 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{b.name}</div>
                        <div className="text-xs ink-subtle truncate mt-0.5">{b.code} · threshold {b.low_stock_threshold} {b.base_unit}</div>
                      </div>
                      <div className="mono ink text-sm font-semibold text-red-500">{b.balance_base} {b.base_unit}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="section-eyebrow mb-4 flex items-center justify-between">
                <span>Recent wastage</span>
                <Link to="/inventory/wastage" className="tiny-label ink-subtle hover:ink">Log wastage →</Link>
              </div>
              {wastage.length === 0 ? (
                <div className="ink-subtle text-sm">No wastage in the last 7 days.</div>
              ) : (
                <div className="divide-y hairline-soft border-y hairline-soft" data-testid="inv-wastage-list">
                  {wastage.map((m) => (
                    <div key={m.id} className="py-3 flex items-center gap-4">
                      <TriangleAlert size={14} className="ink-subtle shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{m.item_name}</div>
                        <div className="text-xs ink-subtle truncate mt-0.5">{m.reason || "—"}{m.notes ? ` · ${m.notes}` : ""}</div>
                      </div>
                      <div className="mono ink text-sm font-semibold">{m.quantity_base} {m.base_unit}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick nav */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-up animate-fade-up-3">
            <NavTile to="/inventory/items" icon={Package} label="Items" desc="Master file · CRUD"/>
            <NavTile to="/inventory/balances" icon={Boxes} label="Balances" desc="Live per-item on-hand"/>
            <NavTile to="/inventory/movements" icon={ArrowUpDown} label="Movements" desc="Append-only ledger"/>
            <NavTile to="/inventory/counts" icon={ClipboardCheck} label="Blind counts" desc="Close the day"/>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ num, label, value, accent, warn }) {
  return (
    <div data-testid={`inv-stat-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono text-2xl font-semibold tracking-tight ${warn ? "text-red-500" : "ink"} ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
    </div>
  );
}

function NavTile({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to} data-testid={`inv-nav-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}
          className="border hairline surface-card rounded-lg p-4 hover:border-foreground/50 transition-colors">
      <Icon size={18} className="ink"/>
      <div className="mt-3 text-[11px] uppercase tracking-[0.24em] font-bold ink">{label}</div>
      <div className="mt-1 text-xs ink-subtle">{desc}</div>
    </Link>
  );
}
